import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export const usePaintCodes = (propertyId = null) => {
    const { user } = useAuth();
    const [paintCodes, setPaintCodes] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchPaintCodes = useCallback(async () => {
        if (!user || !propertyId) {
            setPaintCodes([]);
            setLoading(false);
            return;
        }

        let query = supabase
            .from("paint_codes")
            .select("*")
            .order("created_at", { ascending: false });

        // Filter by property if provided
        if (propertyId) {
            query = query.eq("property_id", propertyId);
        }

        const { data, error } = await query;

        if (error) {
            toast({
                title: "Error",
                description: "Failed to fetch paint codes",
                variant: "destructive",
            });
        } else {
            setPaintCodes(data || []);
        }
        setLoading(false);
    }, [user, propertyId]);

    const addPaintCode = async (paint) => {
        if (!user) return false;

        const { error } = await supabase.from("paint_codes").insert({
            user_id: user.id,
            room: paint.room,
            brand: paint.brand,
            code: paint.code,
            finish: paint.finish,
            last_painted: paint.lastPainted || null,
            property_id: paint.propertyId || propertyId || null,
        });

        if (error) {
            toast({
                title: "Error",
                description: "Failed to add paint code",
                variant: "destructive",
            });
            return false;
        }

        fetchPaintCodes();
        return true;
    };

    const updatePaintCode = async (
        id,
        updates
    ) => {
        const { error } = await supabase
            .from("paint_codes")
            .update({
                room: updates.room,
                brand: updates.brand,
                code: updates.code,
                finish: updates.finish,
                last_painted: updates.lastPainted || null,
            })
            .eq("id", id);

        if (error) {
            toast({
                title: "Error",
                description: "Failed to update paint code",
                variant: "destructive",
            });
            return false;
        }

        toast({
            title: "Success",
            description: "Paint details updated",
        });

        fetchPaintCodes();
        return true;
    };

    const deletePaintCode = async (id) => {
        const { error } = await supabase.from("paint_codes").delete().eq("id", id);

        if (error) {
            toast({
                title: "Error",
                description: "Failed to delete paint code",
                variant: "destructive",
            });
            return false;
        }

        toast({
            title: "Paint Code Deleted",
            description: "The paint code has been removed.",
        });

        fetchPaintCodes();
        return true;
    };

    useEffect(() => {
        let active = true;
        const timer = setTimeout(() => {
            if (active) {
                fetchPaintCodes();
            }
        }, 0);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [fetchPaintCodes]);

    return {
        paintCodes,
        loading,
        addPaintCode,
        updatePaintCode,
        deletePaintCode,
        refetch: fetchPaintCodes,
    };
};
